import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  LinearProgress,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';
import ProductImageUploadModal from './ProductImageUploadModal';

const getImageUrl = (image) => {
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return image;
  return `${api.defaults.baseURL?.replace('/api', '') || ''}${image.startsWith('/') ? image : `/${image}`}`;
};

const formatMoney = (value) => `R$ ${parseFloat(value || 0).toFixed(2)}`;
const hasSizePrices = (produto) => produto?.has_size_prices === true || (
  produto?.has_size_prices == null && Number(produto?.price_p) > 0 && Number(produto?.price_g) > 0
);
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

function Produtos() {
  const theme = useTheme();
  const [produtos, setProdutos] = useState([]);
  const [lanchonetes, setLanchonetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduto, setEditingProduto] = useState(null);
  const [imageUploadModalOpen, setImageUploadModalOpen] = useState(false);
  const [selectedProduto, setSelectedProduto] = useState(null);
  const [customCategories, setCustomCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    price_p: '',
    price_g: '',
    category: 'lanche',
    lanchonete_id: '',
    available: true,
    stock_enabled: false,
    free_delivery: false,
    stock_quantity: 0,
    image: null,
    option_groups: []
  });
  const [optionGroups, setOptionGroups] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAdminMaster, getLanchoneteId } = useAuth();

  // Função para obter o nome da lanchonete
  const getLanchoneteName = (lanchoneteId) => {
    const lanchonete = lanchonetes.find(l => l.id === lanchoneteId);
    return lanchonete ? lanchonete.name : 'Lanchonete não encontrada';
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (isAdminMaster()) {
        // Admin Master vê todos produtos
        const [produtosResponse, lanchonetesResponse, optionGroupsResponse, categoriasResponse] = await Promise.all([
          api.get('/produtos'),
          api.get('/lanchonetes'),
          api.get('/product-options/groups'),
          api.get('/categorias')
        ]);
        setProdutos(produtosResponse.data);
        setLanchonetes(lanchonetesResponse.data);
        setOptionGroups(optionGroupsResponse.data);
        setCustomCategories(categoriasResponse.data);
      } else {
        // Admin Lanchonete vê apenas produtos da sua lanchonete
        const lanchoneteId = getLanchoneteId();
        if (!lanchoneteId) {
          setProdutos([]);
          setOptionGroups([]);
          setCustomCategories([]);
          toast.error('Usuario sem lanchonete associada');
          return;
        }
        const [produtosResponse, optionGroupsResponse, categoriasResponse] = await Promise.all([
          api.get(`/produtos?lanchonete_id=${lanchoneteId}`),
          api.get(`/product-options/groups?lanchonete_id=${lanchoneteId}`),
          api.get(`/categorias?lanchonete_id=${lanchoneteId}`)
        ]);
        setProdutos(produtosResponse.data);
        setOptionGroups(optionGroupsResponse.data);
        setCustomCategories(categoriasResponse.data);
        setFormData(prev => ({ ...prev, lanchonete_id: lanchoneteId }));
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdminMaster, getLanchoneteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenDialog = (produto) => {
    setEditingProduto(produto);
    setImagePreview(null);
    
    // Para admin lanchonete, sempre usar a lanchonete dele
    const lanchoneteId = isAdminMaster() ? '' : getLanchoneteId();
    
    if (produto) {
      setFormData({
        name: produto.name,
        description: produto.description,
        price: produto.price,
        price_p: produto.price_p || '',
        price_g: produto.price_g || '',
        category: produto.category,
        categoria_id: produto.categoria_id || '',
        lanchonete_id: produto.lanchonete_id,
        available: produto.available,
        stock_enabled: Boolean(produto.stock_enabled),
        free_delivery: Boolean(produto.free_delivery),
        stock_quantity: Number(produto.stock_quantity) || 0,
        image: null,
        option_groups: produto.option_groups || [],
        has_sizes: produto.has_size_prices === true || (produto.has_size_prices == null && Number(produto.price_p) > 0 && Number(produto.price_g) > 0),
        wholesale_min_qty: produto.wholesale_min_qty || '',
        wholesale_price: produto.wholesale_price || '',
        has_wholesale: Number(produto.wholesale_min_qty) > 0
      });
      // Se já tem uma imagem, mostrar preview
      if (produto.image) {
        setImagePreview(getImageUrl(produto.image));
      }
    } else {
      const defaultCategory = customCategories.length > 0 ? customCategories[0].name : 'lanche';
      const defaultCategoryId = customCategories.length > 0 ? customCategories[0].id : '';
      
      setFormData({
        name: '',
        description: '',
        price: 0,
        price_p: '',
        price_g: '',
        category: defaultCategory,
        categoria_id: defaultCategoryId,
        lanchonete_id: lanchoneteId,
        available: true,
        stock_enabled: false,
        free_delivery: false,
        stock_quantity: 0,
        image: null,
        option_groups: [],
        has_sizes: false,
        wholesale_min_qty: '',
        wholesale_price: '',
        has_wholesale: false
      });
    }
    setDialogOpen(true);
  };

  const handleImageUpload = (produto) => {
    setSelectedProduto(produto);
    setImageUploadModalOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduto(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      if (name === 'category') {
        const cat = customCategories.find(c => c.name === value);
        if (cat) {
          updated.categoria_id = cat.id;
        } else {
          updated.categoria_id = '';
        }
      }
      
      return updated;
    });
    
    // Se mudou a lanchonete, atualizar o tipo e recarregar categorias daquela lanchonete
    if (name === 'lanchonete_id') {
      const lanchonete = lanchonetes.find(l => l.id === value);
      if (lanchonete) {
        
        api.get(`/categorias?lanchonete_id=${value}`).then(res => {
          setCustomCategories(res.data);
          if (res.data.length > 0) {
            setFormData(prev => ({
              ...prev,
              category: res.data[0].name,
              categoria_id: res.data[0].id
            }));
          } else {
            setFormData(prev => ({
              ...prev,
              category: '',
              categoria_id: ''
            }));
          }
        }).catch(err => {
          console.error('Erro ao buscar categorias da lanchonete:', err);
        });
      }
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error('Escolha uma imagem JPEG, PNG ou WebP.');
        e.target.value = '';
        return;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error('A imagem deve ter no máximo 5 MB.');
        e.target.value = '';
        return;
      }
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const payload = { ...formData };
    const hasSizes = Boolean(payload.has_sizes);
    payload.has_size_prices = Boolean(payload.has_sizes);
    
    if (hasSizes) {
      if (!Number(payload.price)) {
        payload.price = payload.price_p || payload.price_g || 0;
      }
    } else {
      payload.price_p = '';
      payload.price_g = '';
    }

    if (payload.has_wholesale) {
      if (!payload.wholesale_min_qty || !payload.wholesale_price) {
        toast.error('Preencha a quantidade mínima e o preço de atacado.');
        return;
      }
    } else {
      payload.wholesale_min_qty = '';
      payload.wholesale_price = '';
    }
    
    delete payload.has_sizes;
    delete payload.has_wholesale;
    
    const submitFormData = new FormData();
    
    Object.keys(payload).forEach(key => {
      const value = payload[key];
      
      if (key === 'image' && value instanceof File) {
        submitFormData.append('image', value);
      } else if (key === 'option_groups' && Array.isArray(value)) {
        submitFormData.append('option_groups', JSON.stringify(value));
      } else if (key !== 'image' && value !== null && value !== undefined) {
        submitFormData.append(key, value);
      }
    });
    
    try {
      setIsSubmitting(true);
      let produtoResponse;
      
      if (editingProduto) {
        produtoResponse = await api.put(`/produtos/${editingProduto.id}`, submitFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Produto atualizado com sucesso!');
      } else {
        produtoResponse = await api.post('/produtos', submitFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Produto criado com sucesso!');
      }
      
      // Upload extra somente se a imagem nao foi enviada no POST principal
      if (
        formData.image &&
        formData.image instanceof File &&
        !produtoResponse.data?.produto?.image
      ) {
        const produtoId = editingProduto ? editingProduto.id : produtoResponse.data.produto.id;
        const uploadFormData = new FormData();
        uploadFormData.append('image', formData.image);
        
        await api.post(`/produtos/${produtoId}/upload-image`, uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar produto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      try {
        await api.delete(`/produtos/${id}`);
        toast.success('Produto excluído com sucesso!');
        loadData();
      } catch (error) {
        toast.error('Erro ao excluir produto');
      }
    }
  };

  const handleToggleStatus = async (id, available) => {
    try {
      await api.patch(`/produtos/${id}/status`, { available });
      toast.success(`Produto ${available ? 'ativado' : 'desativado'} com sucesso!`);
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Fade in={true}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress 
              size={80} 
              thickness={4}
              sx={{ 
                color: theme.palette.primary.contrastText,
                mb: 2
              }} 
            />
            <Typography variant="h6" color="inherit">
              Carregando produtos...
            </Typography>
            <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
              Preparando suas informações
            </Typography>
          </Box>
        </Fade>
        <LinearProgress 
          sx={{ 
            width: '200px',
            bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
            '& .MuiLinearProgress-bar': {
              bgcolor: theme.palette.primary.contrastText
            }
          }} 
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Produtos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo Produto
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Preço</TableCell>
              <TableCell>Estoque</TableCell>
              {isAdminMaster() && <TableCell>Lanchonete</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {produtos.map((produto) => (
              <TableRow key={produto.id}>
                <TableCell>
                   <Typography variant="subtitle1" fontWeight="bold">
                     {produto.name}
                   </Typography>
                   {produto.free_delivery && <Chip label="Frete grátis" size="small" color="success" />}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {produto.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={produto.category || 'Sem Categoria'} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {hasSizePrices(produto)
                      ? [
                          Number(produto.price_p) > 0 ? `P: ${formatMoney(produto.price_p)}` : null,
                          Number(produto.price_g) > 0 ? `G: ${formatMoney(produto.price_g)}` : null
                        ].filter(Boolean).join(' / ')
                      : formatMoney(produto.price)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={produto.stock_enabled && Number(produto.stock_quantity) === 0 ? 'error' : 'default'}
                    label={produto.stock_enabled ? `${produto.stock_quantity} unidade(s)` : 'Ilimitado'}
                  />
                </TableCell>
                {isAdminMaster() && (
                  <TableCell>
                    <Typography variant="body2">
                      {getLanchoneteName(produto.lanchonete_id)}
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(produto.available)}
                        onChange={(e) => handleToggleStatus(produto.id, e.target.checked)}
                        color="primary"
                      />
                    }
                    label={produto.available ? 'Disponível' : 'Indisponível'}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(produto)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="info"
                    onClick={() => handleImageUpload(produto)}
                  >
                    <ShoppingCartIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(produto.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de criação/edição */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduto ? 'Editar Produto' : 'Novo Produto'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Upload de Imagem */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Imagem do Produto
                </Typography>
                <Box 
                  sx={{ 
                    border: '2px dashed #ccc', 
                    p: 2, 
                    mb: 2, 
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: '#E50914' }
                  }}
                  onClick={() => document.getElementById('product-image-input').click()}
                >
                  {imagePreview ? (
                    <img 
                      src={imagePreview} 
                      alt="Product Preview" 
                      style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8 }}
                    />
                  ) : (
                    <Box>
                      <ShoppingCartIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Clique para adicionar a imagem do produto
                      </Typography>
                    </Box>
                  )}
                </Box>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                  id="product-image-input"
                />
                {formData.image && formData.image instanceof File && (
                  <Typography variant="body2" color="primary">
                    Arquivo selecionado: {formData.image.name}
                  </Typography>
                )}
              </Box>
              
              <TextField
                name="name"
                label="Nome do Produto"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="description"
                label="Descrição"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <FormControl fullWidth required>
                <InputLabel>Categoria</InputLabel>
                <Select
                  name="category"
                  value={formData.category || ''}
                  onChange={handleInputChange}
                  label="Categoria"
                >
                  {customCategories.length === 0 ? (
                    <MenuItem value="" disabled>
                      Nenhuma categoria cadastrada. Crie uma primeiro!
                    </MenuItem>
                  ) : (
                    customCategories.map(cat => (
                      <MenuItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.has_sizes)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        has_sizes: checked,
                        price: checked ? 0 : prev.price,
                        price_p: checked ? prev.price_p : '',
                        price_g: checked ? prev.price_g : ''
                      }));
                    }}
                    color="primary"
                  />
                }
                label="Preços por tamanho (P e G)"
              />

              {!formData.has_sizes ? (
                <TextField
                  name="price"
                  label="Preço"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <AttachMoneyIcon />,
                  }}
                />
              ) : (
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                  <TextField
                    name="price_p"
                    label="Preço Tamanho P"
                    type="number"
                    value={formData.price_p}
                    onChange={handleInputChange}
                    fullWidth
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <AttachMoneyIcon />,
                    }}
                  />
                  <TextField
                    name="price_g"
                    label="Preço Tamanho G"
                    type="number"
                    value={formData.price_g}
                    onChange={handleInputChange}
                    fullWidth
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <AttachMoneyIcon />,
                    }}
                  />
                </Box>
              )}
              
              {/* Desconto por Quantidade (Atacado) */}
              <FormControlLabel
                control={
                  <Switch
                    name="has_wholesale"
                    checked={Boolean(formData.has_wholesale)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        has_wholesale: checked,
                        wholesale_min_qty: checked ? prev.wholesale_min_qty || 5 : '',
                        wholesale_price: checked ? prev.wholesale_price : ''
                      }));
                    }}
                    color="primary"
                  />
                }
                label="Ativar preço de atacado / desconto por quantidade?"
              />

              {formData.has_wholesale && (
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                  <TextField
                    name="wholesale_min_qty"
                    label="Quantidade mínima para desconto"
                    type="number"
                    value={formData.wholesale_min_qty}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    inputProps={{ min: 2, step: 1 }}
                  />
                  <TextField
                    name="wholesale_price"
                    label="Preço unitário no atacado"
                    type="number"
                    value={formData.wholesale_price}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <AttachMoneyIcon />,
                    }}
                  />
                </Box>
              )}
              
              <FormControlLabel
                control={
                  <Switch
                    name="free_delivery"
                    checked={Boolean(formData.free_delivery)}
                    onChange={handleInputChange}
                    color="success"
                  />
                }
                label="Frete grátis quando este produto estiver no pedido"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                Em entregas pelo aplicativo, o cliente não paga o frete e o valor do motoboy fica devido pela lanchonete.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    name="stock_enabled"
                    checked={Boolean(formData.stock_enabled)}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label="Controlar estoque deste produto"
              />

              {formData.stock_enabled && (
                <TextField
                  name="stock_quantity"
                  label="Quantidade em estoque"
                  type="number"
                  value={formData.stock_quantity}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  helperText="Ao chegar a zero, o produto sai do cardápio dos clientes sem ser apagado."
                  inputProps={{ min: 0, step: 1 }}
                />
              )}

              {/* Seletor de Grupos de Opções */}
              <FormControl fullWidth>
                <InputLabel>Grupos de Opções</InputLabel>
                <Select
                  multiple
                  value={formData.option_groups}
                  onChange={(e) => setFormData(prev => ({ ...prev, option_groups: e.target.value }))}
                  label="Grupos de Opções"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={optionGroups.find(g => g.id === value)?.name || value} />
                      ))}
                    </Box>
                  )}
                >
                  {optionGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                  Selecione os grupos de opções que estarão disponíveis para este produto
                </Typography>
              </FormControl>
              
              {isAdminMaster() && (
                <FormControl fullWidth>
                  <InputLabel>Lanchonete</InputLabel>
                  <Select
                    name="lanchonete_id"
                    value={formData.lanchonete_id}
                    onChange={handleInputChange}
                    label="Lanchonete"
                    required
                  >
                    {lanchonetes.map(lanchonete => (
                      <MenuItem key={lanchonete.id} value={lanchonete.id}>
                        {lanchonete.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              <FormControlLabel
                control={
                  <Switch
                    name="available"
                    checked={formData.available}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label="Produto Disponível"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={isSubmitting}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Salvando...' : (editingProduto ? 'Atualizar' : 'Criar')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Modal de Upload de Imagem */}
      <ProductImageUploadModal
        open={imageUploadModalOpen}
        onClose={() => setImageUploadModalOpen(false)}
        productId={selectedProduto?.id}
        onUpload={() => {
          // Aqui você pode tratar o upload da imagem
          // Você pode fazer uma chamada para a API aqui
          toast.success('Imagem do produto atualizada com sucesso!');
          setImageUploadModalOpen(false);
          loadData();
        }}
        title={`Upload de Imagem - ${selectedProduto?.name || 'Novo Produto'}`}
      />
    </Box>
  );
}

export default Produtos;
